import { useEffect, useRef, useState } from "react";
import AppPage from "../AppPage";
import globals from "../../styles/globals.module.css";
import { images } from "../../styles";
import { arrow_Icon, eye_Icon, eyeClosed_Icon } from "../../assets/icons";
import { ApiError } from "../../api";
import { RegisterBody, RegisterErrorCode, AuthResponse } from "../../api/types";
import { useRegister } from "../../hooks/auth";

enum FormField {
  Email = "email",
  Mobile = "mobile",
  Password = "password",
  ConfirmPassword = "confirmPassword",
  PlayerName = "playerName",
  Username = "username",
}

function SignUpPage(props: {
  handleBackButton: () => void;
  handleSignUpSuccess: (data: AuthResponse) => void;
}) {
  const { handleBackButton, handleSignUpSuccess } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);

  const onBack = () => {
    if (isFirstPage) {
      handleBackButton();
    } else {
      setIsFirstPage(true);
    }
  };

  const { mutate: registerUser, isPending, isError, error } = useRegister();
  const [conflictError, setConflictError] = useState<{
    field: "email" | "username";
    message: string;
  } | null>(null);

  if (
    isError &&
    error instanceof ApiError &&
    [0, 400, 404, 500].includes(error.status)
  ) {
    throw error;
  }

  if (isError && !(error instanceof ApiError)) {
    throw error;
  }

  useEffect(() => {
    if (!isError || !(error instanceof ApiError) || error.status !== 409) {
      return;
    }

    if (error.code === RegisterErrorCode.EmailConflict) {
      setConflictError({
        field: "email",
        message: "An account with this email already exists",
      });
      setIsFirstPage(true);
    } else if (error.code === RegisterErrorCode.UsernameConflict) {
      setConflictError({
        field: "username",
        message: "This username is already taken",
      });
    }
  }, [isError, error]);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidMobile = (mobile: string) =>
    /^\+?[\d\s\-(). ]{7,20}$/.test(mobile);

  const isSecurePassword = (password: string) =>
    password.length >= 8 && /\d/.test(password);

  const handleNextPage = () => {
    const form = formRef.current;
    if (!form) return;

    const emailInput = form.elements.namedItem(
      FormField.Email,
    ) as HTMLInputElement;
    const mobileInput = form.elements.namedItem(
      FormField.Mobile,
    ) as HTMLInputElement;
    const passwordInput = form.elements.namedItem(
      FormField.Password,
    ) as HTMLInputElement;
    const confirmPasswordInput = form.elements.namedItem(
      FormField.ConfirmPassword,
    ) as HTMLInputElement;

    const email = emailInput.value.trim();
    const mobile = mobileInput.value.trim();
    const password = passwordInput.value;
    const confirmPassword = confirmPasswordInput.value;

    emailInput.setCustomValidity("");
    mobileInput.setCustomValidity("");
    passwordInput.setCustomValidity("");
    confirmPasswordInput.setCustomValidity("");

    if (!email) {
      emailInput.setCustomValidity("Email is required");
    } else if (!isValidEmail(email)) {
      emailInput.setCustomValidity("Please enter a valid email address");
    }

    if (mobile && !isValidMobile(mobile)) {
      mobileInput.setCustomValidity("Please enter a valid phone number");
    }

    if (password && !isSecurePassword(password)) {
      passwordInput.setCustomValidity(
        "Password must be at least 8 characters and contain at least 1 number",
      );
    }

    if (confirmPassword && password !== confirmPassword) {
      confirmPasswordInput.setCustomValidity("Passwords do not match");
    }

    const page1Valid = [
      emailInput,
      mobileInput,
      passwordInput,
      confirmPasswordInput,
    ].every((input) => input.reportValidity());
    if (!page1Valid) return;
    setIsFirstPage(false);
  };

  const handleUserSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const username = (formData.get(FormField.Username) as string).trim();
    const playerName = (
      (formData.get(FormField.PlayerName) as string) ?? ""
    ).trim();
    const email = (formData.get(FormField.Email) as string).trim();
    const mobile = (formData.get(FormField.Mobile) as string).trim();
    const password = formData.get(FormField.Password) as string;

    if (!username) return;

    const [firstName, ...surnameParts] = playerName.split(" ");
    const surname = surnameParts.join(" ") || undefined;

    const body: RegisterBody = {
      username,
      email,
      password,
      ...(firstName && { firstName }),
      ...(surname && { surname }),
      ...(mobile && { mobileNumber: mobile }),
    };

    registerUser(body, {
      onSuccess: handleSignUpSuccess,
    });
  };

  function renderForm() {
    return (
      <div className={globals["page-scroll-wrapper"]}>
        <div className={globals["page-container"]}>
          <span className={globals["signing-heading"]}>
            Welcome to <br />
            Band Draft!
          </span>
          <p className={globals["signing-subheading"]}>
            Let's get you drafting.
          </p>
          <form
            className={globals["user-form"]}
            onSubmit={handleUserSignup}
            ref={formRef}
          >
            <div style={{ display: isFirstPage ? undefined : "none" }}>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.Email}>Email</label>
                <input
                  type="email"
                  id={FormField.Email}
                  name={FormField.Email}
                  onChange={(e) => {
                    e.currentTarget.setCustomValidity("");
                    if (conflictError?.field === "email")
                      setConflictError(null);
                  }}
                  autoComplete="email"
                />
                {conflictError?.field === "email" && (
                  <p className={globals["user-form__error"]}>
                    {conflictError.message}
                  </p>
                )}
              </div>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.Mobile}>Phone Number</label>
                <input
                  type="tel"
                  id={FormField.Mobile}
                  name={FormField.Mobile}
                  onChange={(e) => e.currentTarget.setCustomValidity("")}
                  autoComplete="tel"
                />
              </div>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.Password}>Password</label>
                <div className={globals["user-form__input-wrapper"]}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id={FormField.Password}
                    name={FormField.Password}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    className={`${globals["button-wrapper"]} ${globals["eye-btn"]}`}
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    <img
                      src={showPassword ? eyeClosed_Icon : eye_Icon}
                      alt=""
                      className={images["eye-icon"]}
                    />
                  </button>
                </div>
              </div>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.ConfirmPassword}>
                  Confirm Password
                </label>
                <div className={globals["user-form__input-wrapper"]}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id={FormField.ConfirmPassword}
                    name={FormField.ConfirmPassword}
                    required
                    autoComplete="new-password"
                    style={{ marginBottom: "10px" }}
                  />
                  <button
                    className={`${globals["button-wrapper"]} ${globals["eye-btn"]}`}
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    <img
                      src={showConfirmPassword ? eyeClosed_Icon : eye_Icon}
                      alt=""
                      className={images["eye-icon"]}
                    />
                  </button>
                </div>
              </div>
              <div className={globals["user-form__submission"]}>
                <button
                  className={globals["button-wrapper"]}
                  type="button"
                  onClick={handleNextPage}
                >
                  <div className={globals["lrg-btn"]}>Next</div>
                </button>
              </div>
            </div>

            <div style={{ display: isFirstPage ? "none" : undefined }}>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.PlayerName}>What's Your Name?</label>
                <input
                  type="text"
                  id={FormField.PlayerName}
                  name={FormField.PlayerName}
                  autoComplete="name"
                />
              </div>
              <div className={globals["user-form__group"]}>
                <label htmlFor={FormField.Username}>
                  What's going to be Your Username?
                </label>
                <p className={globals["username-subheading"]}>
                  This is how you'll appear publically to other
                  <br />
                  people on Band Draft.
                </p>
                {conflictError?.field === "username" && (
                  <p className={globals["user-form__error"]}>
                    {conflictError.message}
                  </p>
                )}
                <input
                  type="text"
                  id={FormField.Username}
                  name={FormField.Username}
                  required
                  autoComplete="username"
                  style={{ marginBottom: "20px" }}
                  onChange={() => {
                    if (conflictError?.field === "username")
                      setConflictError(null);
                  }}
                />
              </div>
              <div className={globals["user-form__submission"]}>
                <button
                  className={globals["button-wrapper"]}
                  type="submit"
                  disabled={isPending}
                >
                  <div className={globals["lrg-btn"]}>
                    {isPending ? "Loading..." : "Sign up"}
                  </div>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <AppPage className={globals["page"]}>
      {renderForm()}
      <button
        className={`${globals["button-wrapper"]} ${globals["back-btn--top-left"]}`}
        onClick={onBack}
      >
        <div className={globals["back-btn"]}>
          <img src={arrow_Icon} alt="" className={images["back-arrow-icon"]} />
        </div>
      </button>
    </AppPage>
  );
}

export default SignUpPage;
